import { makeAutoObservable } from "mobx";
import { RootStore, LangStore } from "../../../store";
import { StoreStatus, CallbackDataParams } from "../../../store/types";
import { getApi, getApiError, isSessionExpired } from "../../../api";
import { ViewEntityStore } from "../../common/store/types";
import { satelliteTaskService } from "../services/satelliteTaskService";
import { satelliteTaskDisplayId } from "./SatelliteTaskCommonStore";
import { SatelliteTask } from "../../../api/graphql";
import { botRunDisplayId } from "../../botRun/store/BotRunCommonStore";
import { botRunService } from "../../botRun/services/botRunService";
import { InitializeParams } from "../../../store/types";

export class SatelliteTaskViewStore implements ViewEntityStore {
  readonly rootStore: RootStore;
  readonly langStore: LangStore;
  status: StoreStatus = "uninitialized";
  storeId?: string;
  originalError: any;
  errorDialogOpened: boolean = false;
  errorTitle: string = "Error";
  errorDescription: string = "Hubo un error";
  service = satelliteTaskService;
  satelliteTaskInstance: SatelliteTask | null = null;
  cardState: Record<string, boolean> = {};
  savedId: string | null = null;
  cancelled = false;
  isDrawerMode = false;
  isModalMode = false;
  callbackParams: CallbackDataParams | null = null;
  idValue: string | undefined;
  taskTypeValue: string = "";
  statusValue: string = "";
  priorityValue: number = 0;
  inputValue: string = "{}";
  outputValue: string = "{}";
  errorValue: string = "{}";
  correlationIdValue: string = "";
  timeoutMsValue: number = 0;
  retryCountValue: number = 0;
  maxRetriesValue: number = 0;
  queuedAtValue: Date | null = null;
  sentAtValue: Date | null = null;
  startedAtValue: Date | null = null;
  completedAtValue: Date | null = null;
  durationValue: number | undefined = undefined;
  botRunService = botRunService;
  botRunSearchValue: string = "";
  botRunIdValue: string = "";
  botRunSelectedValue: { displayId: string; id: string } | null = null;

  constructor(rootStore: RootStore, storeId?: string) {
    this.rootStore = rootStore;
    this.langStore = rootStore.langStore;
    this.storeId = storeId;

    makeAutoObservable(this);
  }

  destroy() {
    // this object will be unusable
  }

  setCallbackParams(callbackParams: CallbackDataParams | null) {
    if (callbackParams?.callerData) {
      this.callbackParams = callbackParams;
    }
  }

  toggleCardState(id: string) {
    this.cardState[id] = !this.cardState[id];
  }

  setCardState(id: string, status: boolean) {
    this.cardState[id] = status;
  }

  getCardState(id: string) {
    if (!(id in this.cardState)) {
      this.cardState[id] = true;
    }
    return !!this.cardState[id];
  }

  *loadSatelliteTaskById(id: string): any {
    const api = yield* getApi(
      this.rootStore.authStore,
      this.rootStore.activityStore
    );
    const result = yield api.satelliteTaskOne({ id });
    this.satelliteTaskInstance = result.satelliteTaskOne;
    if (this.satelliteTaskInstance) {
      this.loadValues(this.satelliteTaskInstance);
    }
  }

  setStatus(status: StoreStatus) {
    this.status = status;
  }

  get processingTitle() {
    return this.langStore.processingTitle;
  }

  get processingDescription() {
    return this.langStore.processingDescription;
  }

  get displayId(): string {
    if (this.satelliteTaskInstance) {
      return satelliteTaskDisplayId(this.satelliteTaskInstance, this.langStore);
    }
    return "";
  }

  get breadcrumbLabel(): string {
    const displayId = this.displayId;
    if (displayId) {
      return displayId;
    }
    return this.langStore.SatelliteTaskSingular;
  }

  closeErrorDialog() {
    this.errorDialogOpened = false;
  }

  setContentTop(_contentTop: number) {}

  *returnedData(params: CallbackDataParams): any {
    if (
      (!params.storeId || params.storeId === this.storeId) &&
      params.returnData &&
      !params.returnData.cancelled &&
      params.returnData.resultId
    ) {
      if (params.callerData?.fieldName === "botRun") {
        const api = yield* getApi(
          this.rootStore.authStore,
          this.rootStore.activityStore
        );
        const result = yield api.botRunOne({ id: params.returnData.resultId });
        const botRun = result.botRunOne;
        this.botRunSelectedValue = botRun;
      }
    }
  }

  showErrorDialog(originalError?: any, title?: string, description?: string) {
    // isSessionExpired y no una comparación a mano: los llamadores pasan a
    // veces el Error crudo y a veces el código ya resuelto, y cada store tenía
    // su propia versión del chequeo —o ninguna—, así que la sesión vencida se
    // detectaba o no según por dónde hubiera entrado el error.
    if (isSessionExpired(originalError, this.langStore)) {
      this.rootStore.authStore.openSessionExpiredDialog(() => {});
      return;
    }

    this.originalError = originalError;
    if (title) {
      this.errorTitle = title;
    }
    if (description) {
      this.errorDescription = description;
    }
    this.errorDialogOpened = true;
  }

  get botRunValue() {
    if (this.botRunSelectedValue) {
      return {
        id: this.botRunSelectedValue.id,
        displayId: botRunDisplayId(this.botRunSelectedValue, this.langStore),
      };
    }
    return null;
  }

  clearFields() {
    this.savedId = null;
    this.cancelled = false;
    this.satelliteTaskInstance = null;
    this.taskTypeValue = "";
    this.statusValue = "";
    this.priorityValue = 0;
    this.inputValue = "{}";
    this.outputValue = "{}";
    this.errorValue = "{}";
    this.correlationIdValue = "";
    this.timeoutMsValue = 0;
    this.retryCountValue = 0;
    this.maxRetriesValue = 0;
    this.queuedAtValue = null;
    this.sentAtValue = null;
    this.startedAtValue = null;
    this.completedAtValue = null;
    this.durationValue = undefined;
    this.botRunSearchValue = "";
    this.botRunIdValue = "";
  }

  close() {
    if (this.isDrawerMode) {
      this.rootStore.closeDrawerView();
    } else if (this.isModalMode) {
      // In modal mode, close or pop the modal instead of navigating
      if (this.rootStore.modalViewStack.length > 1) {
        this.rootStore.popModalView();
      } else {
        this.rootStore.closeModalView();
      }
    } else {
      this.rootStore.router.goBack();
    }
  }

  cancelSatelliteTask() {
    this.cancelled = true;
    this.close();
  }

  get isEditEnabled(): boolean {
    return false;
  }

  editSatelliteTask() {
    const id = this.satelliteTaskInstance?.id;
    if (!id) return;

    if (this.isDrawerMode) {
      this.rootStore.closeDrawerView();
    } else if (this.isModalMode) {
      this.rootStore.closeModalView();
    }
    this.rootStore.router.goTo(
      "/satelliteTask/update/" + id,
      undefined,
      undefined,
      undefined,
      this.breadcrumbLabel
    );
  }

  printSatelliteTask(handlePrint: () => void): void {
    handlePrint();
  }

  *closeSatelliteTask(): any {
    yield this.close();
  }

  loadValues(instance: SatelliteTask) {
    this.idValue = instance.id;
    this.taskTypeValue = instance.taskType;
    this.statusValue = instance.status;
    this.priorityValue = instance.priority;
    this.inputValue = instance.input;
    this.outputValue = instance.output || "";
    this.errorValue = instance.error || "";
    this.correlationIdValue = instance.correlationId;
    this.timeoutMsValue = instance.timeoutMs;
    this.retryCountValue = instance.retryCount;
    this.maxRetriesValue = instance.maxRetries;
    this.queuedAtValue = new Date(instance.queuedAt);
    this.sentAtValue = instance.sentAt ? new Date(instance.sentAt) : null;
    this.startedAtValue = instance.startedAt
      ? new Date(instance.startedAt)
      : null;
    this.completedAtValue = instance.completedAt
      ? new Date(instance.completedAt)
      : null;
    this.durationValue = instance.duration || undefined;
    this.botRunSelectedValue = instance.botRun ? instance.botRun : null;
  }

  getState(): InitializeParams {
    return {
      id: this.idValue,
    };
  }

  *initialize(params?: InitializeParams) {
    try {
      this.status = "processing";

      const id = params?.id;

      if (id) {
        yield this.loadSatelliteTaskById(id);
      }

      this.status = "ok";
    } catch (error) {
      this.status = "error";
      if (error instanceof Error) {
        const apiError = getApiError(error, this.langStore);
        let message = apiError.message;
        this.showErrorDialog(error, apiError.title, message);
      } else {
        this.showErrorDialog(error, this.langStore.serverError, undefined);
      }
    }
  }
}
